import { type ReadonlySignal, signal } from '@preact/signals-core';

export type GanttExtensionId = string;

export interface GanttExtension {
  readonly id: GanttExtensionId;
  readonly priority?: number;
}

export class GanttExtensionRegistry<T extends GanttExtension> {
  private readonly items = new Map<GanttExtensionId, T>();
  private readonly versionSignal = signal(0);

  get version$(): ReadonlySignal<number> {
    return this.versionSignal;
  }

  register(item: T): () => void {
    if (this.items.has(item.id)) {
      throw new Error(`GanttExtension with id "${item.id}" already registered`);
    }
    this.items.set(item.id, item);
    this.versionSignal.value = this.versionSignal.value + 1;
    return () => {
      this.unregister(item.id);
    };
  }

  unregister(id: GanttExtensionId): boolean {
    const removed = this.items.delete(id);
    if (removed) {
      this.versionSignal.value = this.versionSignal.value + 1;
    }
    return removed;
  }

  list(): T[] {
    return [...this.items.values()].sort(
      (a, b) => (a.priority ?? 0) - (b.priority ?? 0)
    );
  }

  get(id: GanttExtensionId): T | undefined {
    return this.items.get(id);
  }

  size(): number {
    return this.items.size;
  }
}
